import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "urql";

import { TrialForm } from "@/components/forms/TrialForm";
import { TrialInput, zTrialInput } from "@/components/forms/validation/trial";
import { buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { RoundT } from "@/routes/round/queries";
import { cn } from "@/utils";

const createTrialMutation = graphql(`
    mutation createTrial($input: CreateTrialInput!) {
        createTrial(input: $input) {
            id
            ...useTrial_Trial
        }
    }
`);

export const TrialCreateDialog = ({ onCreated }: { onCreated?: () => void }) => {
    const [_res, createTrial] = useMutation(createTrialMutation);
    const { round } = useOutletContext<{
        round: RoundT;
    }>();

    const [isOpen, setIsOpen] = useState(false);

    const onSubmit: SubmitHandler<TrialInput> = async input => {
        if (!input.player1Id || !input.player2Id) {
            toast.error("Both players are required");
            return;
        }
        const result = await createTrial({
            input: {
                roundId: round.id,
                player1Id: input.player1Id,
                player2Id: input.player2Id,
                result: input.result,
                finishedAt: input.finishedAt ? new Date(input.finishedAt).toISOString() : null,
            },
        });

        if (result.error) {
            toast.error("Unable to create trial", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
        } else {
            if (onCreated != null) {
                onCreated();
            }
            toast.success("Trial was created");
            setIsOpen(false);
        }
    };

    const defaultValues = zTrialInput.parse({
        player1Id: null,
        player2Id: null,
        finishedAt: null,
        result: null,
    });

    return (
        <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
            <DialogTrigger className={cn(buttonVariants())}>
                <PlusIcon />
            </DialogTrigger>
            <DialogContent
                className="max-h-[80vh] w-full max-w-3xl overflow-y-auto"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <div className="mr-4 flex items-center justify-between">
                        <DialogTitle>Edit trial</DialogTitle>
                    </div>
                </DialogHeader>
                <TrialForm onSubmit={onSubmit} defaultValues={defaultValues} />
            </DialogContent>
        </Dialog>
    );
};
